import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

export default function AboutContentPage() {
  const [story, setStory] = useState(""); // textarea: paragraphs separated by blank lines
  const [pillars, setPillars] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/about")) || {};
      setStory((d.story || []).join("\n\n"));
      setPillars(d.pillars || []);
      setTimeline(d.timeline || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updPillar = (i, f, v) => setPillars(pillars.map((p, idx) => (idx === i ? { ...p, [f]: v } : p)));
  const updTl = (i, f, v) => setTimeline(timeline.map((t, idx) => (idx === i ? { ...t, [f]: v } : t)));
  const moveTl = (i, dir) =>
    setTimeline((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const c = [...arr];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const payload = {
        story: story.split(/\n\s*\n+/).map((s) => s.trim()).filter(Boolean),
        pillars: pillars.filter((p) => (p.label || "").trim()),
        timeline: timeline.filter((t) => (t.year || "").trim() || (t.text || "").trim()),
      };
      const saved = await adminPut("/admin/content/about", payload);
      if (saved) {
        setStory((saved.story || []).join("\n\n"));
        setPillars(saved.pillars || []);
        setTimeline(saved.timeline || []);
      }
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      {/* Story */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead title="Our Story" action={<span className="text-xs text-[#9aa]">Leave a blank line between paragraphs</span>} />
        <div className="p-[22px]">
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            rows={8}
            placeholder="Write the school's story…"
            className={`${inputCls} resize-y leading-relaxed`}
          />
        </div>
      </AdminCard>

      {/* Pillars */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Mission / Vision / Values"
          action={
            <button
              onClick={() => setPillars([...pillars, { icon: "🎯", label: "", text: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {pillars.length === 0 && <div className="text-muted text-sm">None yet.</div>}
          {pillars.map((p, i) => (
            <div key={i} className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[60px_1fr_auto] gap-3 items-start">
              <input value={p.icon || ""} onChange={(e) => updPillar(i, "icon", e.target.value)} placeholder="🎯" className={`${inputCls} text-center`} />
              <div className="flex flex-col gap-2">
                <input value={p.label || ""} onChange={(e) => updPillar(i, "label", e.target.value)} placeholder="Title (e.g. Mission)" className={inputCls} />
                <textarea value={p.text || ""} onChange={(e) => updPillar(i, "text", e.target.value)} placeholder="Text" rows={2} className={`${inputCls} resize-y`} />
              </div>
              <button onClick={() => setPillars(pillars.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]">🗑</button>
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Timeline */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="History Timeline"
          action={
            <button
              onClick={() => setTimeline([...timeline, { year: "", text: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Milestone
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {timeline.length === 0 && <div className="text-muted text-sm">No milestones yet.</div>}
          {timeline.map((t, i) => (
            <div key={i} className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[110px_1fr_auto] gap-3 items-start">
              <input value={t.year || ""} onChange={(e) => updTl(i, "year", e.target.value)} placeholder="Year" className={inputCls} />
              <textarea value={t.text || ""} onChange={(e) => updTl(i, "text", e.target.value)} placeholder="What happened" rows={2} className={`${inputCls} resize-y`} />
              <div className="flex md:flex-col gap-1.5">
                <button onClick={() => moveTl(i, -1)} title="Up" className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs">↑</button>
                <button onClick={() => moveTl(i, 1)} title="Down" className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs">↓</button>
                <button onClick={() => setTimeline(timeline.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2 py-1 cursor-pointer text-xs text-[#c44]">🗑</button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
